import {
  GenericPromiseRejectionErrorMessage,
  JsonFormatParams,
  PartnerName
} from './types'

export const getGenericRuntimeErrorMessage = (
  { message, filename, lineno, colno, error }: ErrorEvent,
  stack?: string
) =>
  `ErrorMessage: ${message} Script: ${filename} Line: ${lineno} Column: ${colno} StackTrace: ${stack ||
    error}`

export const getGenericPromiseRejectionErrorMessage = ({
  message
}: GenericPromiseRejectionErrorMessage) => `ErrorMessage: ${message}`

export const isPlainObject = (obj: any) =>
  Object.prototype.toString.call(obj) === '[object Object]'

let CIRCULAR_ERROR_MESSAGE: string

// https://github.com/nodejs/node/blob/master/lib/util.js
export const tryStringify = (arg: any) => {
  try {
    return JSON.stringify(arg)
  } catch (error) {
    // Populate the circular error message lazily
    if (!CIRCULAR_ERROR_MESSAGE) {
      try {
        const a = {}
        // @ts-ignore
        a.a = a
        JSON.stringify(a)
      } catch (circular) {
        CIRCULAR_ERROR_MESSAGE = (circular as Error).message
      }
    }

    if ((error as Error).message === CIRCULAR_ERROR_MESSAGE) {
      return '[Circular]'
    }

    throw error
  }
}

export const getPartnerName = (): PartnerName =>
  window.sessionStorage.getItem('partnerName') || window.partnerName

export const defaultJSONFormat = ({
  message,
  methodName,
  logLevel,
  loggerName
}: JsonFormatParams) =>
  tryStringify({
    ...(typeof message !== 'string' && isPlainObject(message)
      ? message
      : { message }),
    logger: loggerName || '',
    logLevel,
    logMethod: (methodName || '').toUpperCase()
  })

export class Deferred<T> {
  public promise: Promise<T>

  private fate: 'resolved' | 'unresolved'
  private state: 'pending' | 'fulfilled' | 'rejected'

  private _resolve!: (value: T | PromiseLike<T>) => void
  private _reject!: (reason?: any) => void

  constructor() {
    this.state = 'pending'
    this.fate = 'unresolved'
    this.promise = new Promise((resolve, reject) => {
      this._resolve = resolve
      this._reject = reject
    })

    this.promise.then(
      () => (this.state = 'fulfilled'),
      () => (this.state = 'rejected')
    )
  }

  resolve(value?: any) {
    if (this.fate === 'resolved') {
      throw Error('Deferred cannot be resolved twice')
    }

    this.fate = 'resolved'

    this._resolve(value)
  }

  reject(reason?: any) {
    if (this.fate === 'resolved') {
      throw Error('Deferred cannot be resolved twice')
    }

    this.fate = 'resolved'

    this._reject(reason)
  }

  isResolved() {
    return this.fate === 'resolved'
  }

  isPending() {
    return this.state === 'pending'
  }

  isFulfilled() {
    return this.state === 'fulfilled'
  }

  isRejected() {
    return this.state === 'rejected'
  }
}
